import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { CmsImageData } from '@fcom/core-api/interfaces';

import { Amount } from './dapi.interface';
import {
  Category,
  FinnairAmount,
  FinnairBoundItem,
  FinnairCodeAndName,
  FinnairLowestPrice,
  FinnairPassengerCode,
  FinnairPassengerServiceItem,
  FinnairPassengerServiceSelectionItem,
  FinnairServiceCatalogCampaign,
  FinnairServiceItem,
  FinnairServiceSegmentItem,
  FinnairServiceCatalogEligibility,
  FinnairServiceVariantItemV3,
  PaxLevelQuota,
  SubCategory,
} from '../api/models';
import { SeatCharacteristics } from './cart.interface';

export type ServiceAvailabilityPerFragment = Record<string, boolean>;

export enum ServicesStatus {
  /**
   * No request for services has been placed.
   */
  INITIAL,

  /**
   * A request for fetching the services is ongoing.
   */
  PENDING,

  /**
   * The request for the services failed.
   */
  LOAD_ERROR,

  /**
   * The services request passed succesfully and should be in the state.
   */
  OK,
}

export interface ServiceAvailability {
  [Category.CABIN_BAGGAGE]: ServiceAvailabilityPerFragment;
  [Category.BAGGAGE]: ServiceAvailabilityPerFragment;
  [Category.MEAL]: ServiceAvailabilityPerFragment;
  [Category.WIFI]: ServiceAvailabilityPerFragment;
  [Category.LOUNGE]: ServiceAvailabilityPerFragment;
  [Category.COVER]: ServiceAvailabilityPerFragment;
  [Category.SPORT]: ServiceAvailabilityPerFragment;
  [Category.PET]: ServiceAvailabilityPerFragment;
  [Category.SAF]: ServiceAvailabilityPerFragment;
  [Category.PRIORITY]: ServiceAvailabilityPerFragment;
}

export interface BaseService {
  /**
   * The semantic identifier of the service.
   * <p/>
   * Combined with the type of the service (baggage/meals), this uniquely identifies the type of the service.
   * Can be used for detecting whether a service is the same for multiple bounds or flights.
   * <p/>
   * Can be used as a localization key in combination with the service type.
   */
  variant: string;

  /**
   * The top-level category of the service
   */
  category: Category;
}

export interface CartService extends BaseService {
  /**
   * Category name
   */
  subCategory: string;

  /**
   * The identifier of the cart's traveler to whom this service is assigned to.
   */
  travelerId: string;

  /**
   * The desired services count, e.g., amount of extra bags.
   */
  quantity: number;

  /**
   * The price for a single item. Total price can be calculated from quantity * price.
   */
  price?: Amount;

  /**
   * Flight ids this service is attached to
   */
  flightIds: string[];
}

export interface ServiceCatalogTranslations {
  title: string;
  description?: string;
  detailText?: string;

  /** Ingredients if meal service */
  ingredients?: string;
  /** HTML content describing the nutrition facts for meal service. Contains title. */
  nutrition?: string;
  /** meal categories, e.g. low-lactose. These need to be translated. */
  itemCategory?: string[];

  /** Specific to cancellation cover and SAF */
  disclaimerText?: string;
  actionText?: string;
}

export interface ServiceCatalogService extends Omit<FinnairServiceVariantItemV3, 'translations' | 'media'> {
  /**
   * The category of the service
   */
  category: Category;

  /**
   * The child-level category of the service
   */
  subCategory: SubCategory;

  /**
   * Translations for category coming from cms
   */
  translations: ServiceCatalogTranslations;

  /**
   * Picture for category coming from cms
   */
  media?: CmsImageData;

  /**
   * If discount is given then this is the original price if we know it
   */
  originalPrice?: Amount;

  isPaid?: boolean;
  isTicketed?: boolean;
  isModifiable?: boolean;
  seatNumber?: string;
}

export type ServicesPerTraveler = Record<string, ServiceCatalogService[]>;

export type ServiceCatalogServices = Record<string, ServicesPerTraveler>;

export type SelectionsPerTraveler<T> = Record<string, T>;

export type ServiceCatalogSelections<T> = Record<string, SelectionsPerTraveler<T>>;

export type ServiceCatalogSelectionsForAllTravelers = Record<string, TravelerService[]>;

export type ServiceCatalogCategoryServicesMap = Record<string, ServiceCatalogServices>;

export interface ServiceCatalogCategory {
  category: Category;
  services: ServiceCatalogServices;
  translations: ServiceCatalogTranslations;
  media?: CmsImageData;
  lowestPrice?: Record<string, Record<string, FinnairLowestPrice>>;
}

export interface ServiceCatalog {
  messages: string[];
  categories: ServiceCatalogCategory[];
  campaigns?: FinnairServiceCatalogCampaign[];
  paxLevelQuota: PaxLevelQuota;
}

export interface ServiceSelections {
  [Category.BAGGAGE]?: ServiceCatalogSelections<TravelerService[]>;
  [Category.CABIN_BAGGAGE]?: ServiceCatalogSelections<TravelerService[]>;
  [Category.WIFI]?: ServiceCatalogSelections<TravelerService>;
  [Category.PRIORITY]?: ServiceCatalogSelections<TravelerService[]>;
  [Category.LOUNGE]?: ServiceCatalogSelections<TravelerService[]>;
  [Category.COVER]?: ServiceCatalogSelections<TravelerService[]>;
  [Category.MEAL]?: ServiceCatalogSelections<TravelerService>;
  [Category.PET]?: ServiceCatalogSelections<TravelerService>;
  [Category.SPORT]?: ServiceCatalogSelections<TravelerService>;
  [SubCategory.SEAT_HOT_PRE_ORDER_MEAL]?: ServiceCatalogSelections<TravelerService[]>;
  [SubCategory.SEAT_PRIORITY_SERVICES]?: ServiceCatalogSelections<TravelerService[]>;
  [SubCategory.SEAT_FRESH_MEAL]?: ServiceCatalogSelections<TravelerService[]>;
  [SubCategory.SEAT_WIFI]?: ServiceCatalogSelections<TravelerService[]>;
  [SubCategory.SEAT_CABIN_BAGGAGE]?: ServiceCatalogSelections<TravelerService[]>;
}

export enum CustomServiceType {
  JOURNEY = 'JOURNEY',
  GROUP = 'GROUP',
}

export interface TravelerService {
  category: SubCategory;

  /**
   * Identifies the type of the service in combination with service category.
   * @see BaseService#variant
   */
  variant: string;
  quantity: number;
  /**
   * If discount is given this is the original price
   */
  originalPricePerItem?: Amount;
  pricePerItem: Amount;
  tieredPricePerItem?: Array<{ pricePerItem: Amount; maxNumberItems: number }>;
  includedInTicketType: boolean;
  includedInTierBenefit: boolean;

  displayName: string;
  paid?: boolean;
  ticketed?: boolean;
  modifiable?: boolean;
  travelerId?: string;
  /**
   * Id is needed when adding bundle products to cart
   */
  id?: string;

  /**
   * Extra fields for translations
   */
  title?: string;
  seatNumber?: string;
  maxBaggageWeight?: string;
  // The ones below are likely to change with future bundles, but until then...
  seatTypeLabel?: string;
  seatCharacteristics?: SeatCharacteristics[];
  includedProductVariant?: string;
}

export interface ServicesState {
  status: ServicesStatus;
  services?: ServiceCatalog;
  selections?: ServiceSelections;
  upsell?: SelectedServiceData;
  campaignsShown: string[];
  serviceAvailability?: ServiceAvailability;
  bundleEligibilities?: FinnairServiceCatalogEligibility;
}

export interface ServicePassenger extends FinnairPassengerServiceItem {
  withInfant: boolean;
  withInfantFullName: string;
}

export interface Upsell {
  enabled: boolean;
  articleKey: string;
  expand: boolean;
  lowestPrice: FinnairAmount | null;
  isShowMoreBtn: boolean;
  updatingService: boolean;
  hasPlusTierBenefit: boolean;
}

export interface BoundWithServices extends FinnairBoundItem {
  services: FinnairServiceItemWithUpsell[];
}

export interface FinnairServiceItemWithUpsell extends FinnairServiceItem {
  upsell?: Upsell;
  complimentaryService?: FinnairPassengerServiceSelectionItem[];
  notIncludedServices?: boolean;
  unpaidQuantity?: number;
}

export type UpsellCategoriesForBound = Record<string, Record<string, boolean>>;

export interface SelectedServiceData {
  category: Category;
  index?: number;
  fragmentId?: string;
  paxId?: string;
}

export enum ServiceStatus {
  CONFIRMED = 'confirmed',
  PENDING_PAYMENT = 'pendingPayment',
  AVAILABLE_FOR_SELL = 'availableForSell',
  ASSIGN_AT_AIRPORT = 'assignAtAirport',
}

type GroupedServicesSeatType = 'confirmed' | 'pendingPayment' | 'availableForSell' | 'assignAtAirport';

export interface GroupedServices {
  key: GroupedServicesSeatType;
  icon?: SvgLibraryIcon.SHOPPING_CART | SvgLibraryIcon.CHECKMARK | SvgLibraryIcon.TIME_ZONE;
  services: FinnairPassengerServiceSelectionItemWithStatus[];
  showActionsButtons: boolean;
  tierLevel?: string;
}

export interface FinnairPassengerServiceSelectionItemWithStatus extends FinnairPassengerServiceSelectionItem {
  modifyInProgress?: boolean;
}

interface ServicesForPassenger extends FinnairPassengerServiceItem {
  groupedServices?: GroupedServices[];
  passengerTypeCode?: FinnairPassengerCode;
}

export interface FlightServices extends Omit<FinnairServiceSegmentItem, 'passengers'> {
  operatingAirline?: FinnairCodeAndName;
  passengers?: ServicesForPassenger[];
  complimentaryServices: FinnairPassengerServiceSelectionItem[];
}

export interface ModifiedServiceInfo {
  category: Category;
  variant?: string;
  travelerId?: string;
  fragmentId?: string;
  boundId?: string;
  seatNumber?: string;
}

export interface ModifiedServiceProgress {
  services?: ModifiedServiceInfo[];
  modifyInProgress: boolean;
}
